import type { JavascriptSerializable, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CollectionCodeKey } from './types';
import { getCollectionFields, getCollectionPrimaryKeyFields, getStaticDefaultValue, PRIMITIVE_TYPES_NUMBER } from '#layers/data-model/public';

/**
 * Creates a conservative example filter used when a bulk operation has no explicit filter.
 */
export function getDefaultCodeFilter(collection: ResolvedMonospaceCollection): { [key: string]: JavascriptSerializable } {
	const primaryKeyFields = getCollectionPrimaryKeyFields(collection);
	let fields = primaryKeyFields;

	if (fields.length === 0) {
		fields = getCollectionFields(collection, {
			includeRelationFields: false,
			includeUnsupportedFields: false,
		});
	}

	const [field] = fields;

	if (!field) {
		return {};
	}

	return {
		[field.apiName]: {
			_eq: getDefaultPrimitiveFieldValue(field),
		},
	};
}

/**
 * Returns a placeholder key shaped like the collection primary key.
 */
export function getDefaultCodeKey(collection: ResolvedMonospaceCollection): CollectionCodeKey {
	const primaryKeyFields = getCollectionPrimaryKeyFields(collection);

	if (primaryKeyFields.length === 0) {
		return '<key>';
	}

	if (primaryKeyFields.length === 1) {
		return getDefaultPrimitiveFieldValue(primaryKeyFields[0]!);
	}

	return Object.fromEntries(primaryKeyFields.map((field) => [field.apiName, getDefaultPrimitiveFieldValue(field)]));
}

/**
 * Chooses a primitive placeholder that matches the broad field type.
 */
export function getDefaultPrimitiveFieldValue(field: { apiName: string; type?: { name?: string } }): string | number | boolean {
	if (field.type?.name && isNumericPrimitiveTypeName(field.type.name)) {
		return 1;
	}

	if (field.type?.name === 'boolean') {
		return true;
	}

	return `<${field.apiName}>`;
}

/**
 * Checks whether a primitive type name should use a numeric placeholder.
 */
function isNumericPrimitiveTypeName(typeName: string): boolean {
	return PRIMITIVE_TYPES_NUMBER.includes(typeName as typeof PRIMITIVE_TYPES_NUMBER[number]);
}

/**
 * Chooses a write placeholder, preferring static schema defaults when available.
 */
export function getDefaultPrimitiveFieldWriteValue(field: { apiName: string; type?: { name?: string }; defaultValue?: unknown }): JavascriptSerializable {
	const staticDefaultValue = getStaticDefaultValue(field.defaultValue as Parameters<typeof getStaticDefaultValue>[0]);

	return staticDefaultValue === undefined ? getDefaultPrimitiveFieldValue(field) : staticDefaultValue as JavascriptSerializable;
}
